import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { createAuthHeaders, getCurrentUserId } from '../../utils/authUtils';
import { normalizePhone } from '../../utils/validation';
import { Button, Alert, Loading, Modal } from '../common';
import { useBusinessConfig } from '../../context/BusinessConfigContext';

const CREDIT_AMOUNTS = [50, 100, 200, 500];

const CreditPurchase = () => {
  const { config } = useBusinessConfig();
  const [userId, setUserId] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentUrl, setPaymentUrl] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPurchaseOptions, setShowPurchaseOptions] = useState(false);

  // Get current credit and user phone from config
  const currentCredit = config?.credits || 0;
  const userPhone = config?.phone || config?.business_phone || '0000000000';

  // Initialize user ID
  useEffect(() => {
    try {
      const uid = getCurrentUserId();
      setUserId(uid);
    } catch (err) {
      setError('שגיאה באימות משתמש. אנא התחבר מחדש.');
      console.error('Error initializing user:', err);
    }
  }, []);

  // Subscribe to payment status changes
  useEffect(() => {
    if (!paymentId) return;

    const paymentDocRef = doc(db, 'paymentsTracking', 'clinicQcredit', 'payments', paymentId);
    const unsubscribe = onSnapshot(
      paymentDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPaymentStatus(data.status || '');

          if (data.status === 'completed') {
            setLoading(false);
            setSuccessMessage('התשלום בוצע בהצלחה! הקרדיט התעדכן.');
            setTimeout(() => {
              resetToInitialState();
            }, 3000);
          } else if (data.status === 'failed') {
            setError('התשלום נכשל. אנא נסה שוב.');
            setLoading(false);
          }
        }
      },
      (error) => {
        console.error('Error listening to payment status:', error);
      }
    );

    return () => unsubscribe();
  }, [paymentId]);

  const resetToInitialState = () => {
    setSelectedAmount(null);
    setPaymentUrl('');
    setPaymentId('');
    setPaymentStatus('');
    setError('');
    setSuccessMessage('');
    setLoading(false);
  };

  const closeModal = () => {
    setShowPurchaseOptions(false);
    resetToInitialState();
  };

  const handlePurchase = async () => {
    if (!selectedAmount) {
      setError('אנא בחר כמות קרדיט לרכישה');
      return;
    }

    if (!config?.business_name) {
      setError('שם העסק חסר. אנא עדכן את פרטי העסק בהגדרות.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const headers = await createAuthHeaders();
      const orderNumber = `CREDIT-${Date.now()}`;

      const response = await fetch(
        `${process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL}/clinicQcreditPayment`,
        {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            operation: 'createPaymentLink',
            businessName: 'clinicQcredit',
            customerName: config.business_name,
            customerPhone: normalizePhone(userPhone),
            price: selectedAmount,
            orderNumber: orderNumber,
            userId: userId,
            products: {
              data: [{
                catalogNumber: orderNumber,
                name: 'רכישת קרדיט עבור קמפיינים',
                price: selectedAmount,
                quantity: 1,
                minQuantity: 1
              }]
            }
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        setPaymentUrl(data.data.paymentUrl);
        setPaymentId(data.data.paymentId);
      } else {
        setError(data.error || 'שגיאה ביצירת קישור תשלום');
        setLoading(false);
      }
    } catch (err) {
      setError('שגיאה בחיבור לשרת');
      setLoading(false);
      console.error('Error creating payment link:', err);
    }
  };

  const openPaymentLink = () => {
    if (paymentUrl) {
      window.open(paymentUrl, '_blank');
    }
  };

  if (!userId) {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-100 to-purple-100 rounded-lg">
        <Loading />
      </div>
    );
  }

  return (
    <>
      {/* Folded state - horizontal layout */}
      <div className="flex lg:inline-flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-pink-100 to-purple-100 rounded-lg w-full lg:w-auto justify-center">
        <span className="text-gray-600">יתרת קרדיט נוכחית:</span>
        <span className="text-2xl font-bold text-pink-600">{currentCredit}</span>
        <span className="text-gray-600">₪</span>
        <Button
          onClick={() => setShowPurchaseOptions(true)}
          variant="primary"
          size="sm"
          className="mr-2 px-4 py-2"
        >
          טען עוד
        </Button>
      </div>

      {/* Modal with purchase options */}
      <Modal
        isOpen={showPurchaseOptions}
        onClose={closeModal}
        title="רכישת קרדיט"
      >
        <div className="space-y-6">
          {/* Current credit display in modal */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-100 to-purple-100 rounded-lg">
              <span className="text-gray-600">יתרת קרדיט נוכחית:</span>
              <span className="text-2xl font-bold text-pink-600">{currentCredit}</span>
              <span className="text-gray-600">₪</span>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <Alert variant="success">
              {successMessage}
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert variant="error">
              {error}
            </Alert>
          )}

          {/* Payment link section */}
          {paymentUrl ? (
            <div className="space-y-4">
              <Alert variant="info">
                קישור תשלום נוצר בהצלחה. לחץ על הכפתור למטה לביצוע התשלום.
              </Alert>

              <Button
                onClick={openPaymentLink}
                variant="primary"
                className="w-full py-3"
                size="lg"
              >
                פתח קישור תשלום
              </Button>

              {paymentStatus && paymentStatus !== 'completed' && (
                <div className="text-center text-sm text-gray-600">
                  סטטוס תשלום: {paymentStatus === 'pending' ? 'ממתין לתשלום' : paymentStatus}
                </div>
              )}

              <Button
                onClick={resetToInitialState}
                variant="secondary"
                className="w-full"
              >
                ביטול
              </Button>
            </div>
          ) : (
            <>
              {/* Credit amount selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  בחר כמות קרדיט לרכישה
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {CREDIT_AMOUNTS.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setSelectedAmount(amount)}
                      disabled={loading}
                      className={`
                        relative p-6 rounded-xl border-2 transition-all duration-200
                        ${selectedAmount === amount
                          ? 'border-pink-500 bg-gradient-to-br from-pink-50 to-purple-50 shadow-lg'
                          : 'border-gray-200 bg-white hover:border-pink-300 hover:shadow-md'
                        }
                        ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      <div className="text-center">
                        <div className="text-3xl font-bold text-gray-800 mb-1">
                          {amount}
                        </div>
                        <div className="text-sm text-gray-600">₪</div>
                      </div>
                      {selectedAmount === amount && (
                        <div className="absolute top-2 right-2">
                          <div className="w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Purchase button */}
              <Button
                onClick={handlePurchase}
                disabled={!selectedAmount || loading}
                variant="primary"
                className="w-full py-3"
                size="lg"
              >
                {loading ? 'יוצר קישור תשלום...' : 'המשך לתשלום'}
              </Button>
            </>
          )}

          {/* Info section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">מידע חשוב:</h3>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>הקרדיט יתעדכן אוטומטית לאחר השלמת התשלום</li>
              <li>ניתן לעקוב אחר סטטוס התשלום במסך זה</li>
              <li>בעיה בתשלום? צור קשר עם התמיכה</li>
            </ul>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CreditPurchase;
